import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useI18n } from '../i18n';

const Bar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: saturate(180%) blur(12px);
  -webkit-backdrop-filter: saturate(180%) blur(12px);
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.md};
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  z-index: 1200;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1rem;
    flex-direction: column;
    align-items: stretch;
  }
`;

const Text = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.muted};
  max-width: 720px;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    justify-content: flex-end;
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  min-height: 40px;
  border-radius: ${({ theme }) => theme.radii.sm}px;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: background ${({ theme }) => theme.transition.base},
    color ${({ theme }) => theme.transition.base},
    border-color ${({ theme }) => theme.transition.base};

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusRing};
  }
`;

const Primary = styled(Button)`
  background: ${({ theme }) => theme.colors.brand};
  color: #fff;
  &:hover {
    background: ${({ theme }) => theme.colors.brandDark};
  }
`;

const Secondary = styled(Button)`
  background: transparent;
  color: ${({ theme }) => theme.colors.muted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  &:hover {
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.muted};
  }
`;

const COOKIE_KEY = 'berrik_cookie_consent';

const CookieConsent = () => {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(COOKIE_KEY);
      if (!stored) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const saveChoice = (value) => {
    try {
      window.localStorage.setItem(COOKIE_KEY, JSON.stringify({ value, t: Date.now() }));
    } catch {
      // ignore
    }
  };

  const handleAccept = () => {
    saveChoice('accepted');
    setVisible(false);
    try {
      window.dispatchEvent(new Event('cookie-consent-accepted'));
    } catch {}
  };

  const handleReject = () => {
    saveChoice('rejected');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Bar role="region" aria-label="Cookie consent">
      <Text>{t('cookies.text')}</Text>
      <Actions>
        <Secondary type="button" onClick={handleReject}>{t('cookies.reject')}</Secondary>
        <Primary type="button" onClick={handleAccept}>{t('cookies.accept')}</Primary>
      </Actions>
    </Bar>
  );
};

export default CookieConsent;
